import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { TreeModule, TreeNode, SelectItem } from 'primeng';
import { IUserOrganisationLevel } from '../interfaces/user-organisation-level';
import { EditService } from './edit.service';
import { HttpResponsesHandlersFactory } from '../handlers/http-responses-handler';
import { IOrganisationFilter } from '../interfaces/organisation-filter';
import {
  IOrganisationSpecificStepGroup,
  IOrganisationSpecificSteps,
} from '../interfaces/organisation-specific-steps';
import { LoggerService } from './logger.service';
import { TaggedLogger } from './taggedLogger';
import { IContractTypeHistory } from '../interfaces/contract-type-history.interface';
import { IOrganisationTelephonyInfo } from '../interfaces/telephony/organisation-telephony-info';
import { IServiceDeliveryGroup } from '../interfaces/service-delivery-group';
import { ICallerInfo } from '../interfaces/telephony/caller-info';
import { IBasicOrganisationInfo } from '../interfaces/telephony/basic-organisation-info';
import { ICustomer } from '../interfaces/customer';
import { IERegion } from '../interfaces/eregion';
import { IFacetedSupplier } from '../interfaces/faceted-inbox-task';
import { IHandoverTime } from '../interfaces/handover-time';
import { SupplierSignupTypeInterface } from '../interfaces/supplier-signup-type.interface';
import { SupplierSignupTypeEnum } from '../enums/supplier-signup-type.enum';
import { IOrganisationPhoneNumber } from '../interfaces/telephony/organisation-phone-number';
import { IDriverHomeInfo } from '../interfaces/driver-location';
import { Operation } from 'fast-json-patch';
import { IImpersonationUserInfo } from '../interfaces/impersonation-user-info.dto.interface';
import { IOrganisationActivationData } from '../interfaces/organisation-activation-data.interface';
import { IOrganisationInfo } from '../interfaces/organisation-info';
import { AllocateDriverDTO } from '../interfaces/allocate-driver';
import { IDriverDTO } from '../interfaces/driver';

@Injectable({ providedIn: 'root' })
export class OrganisationsService extends EditService<any, null> {
  private readonly logger: TaggedLogger;
  readonly url = '/api/org';

  entityTitle = '';

  constructor(
    http: HttpClient,
    handlersFactory: HttpResponsesHandlersFactory,
    loggerService: LoggerService,
  ) {
    super(http, handlersFactory);
    this.logger = loggerService.taggedLogger('service:organisations');
  }

  public getSupplierSignupTypes(): Promise<SupplierSignupTypeInterface[]> {
    return this.handleRequest(
      this.http.get<SupplierSignupTypeInterface[]>(`${this.url}/supplierSignupTypes`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public get(): Promise<TreeNode[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/structure`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/structure`).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public getSupplierHomeLocations(forDate): Promise<IDriverHomeInfo[]> {
    return this.http
      .get<IDriverHomeInfo[]>(`${this.url}/supplierHomeLocations`, { params: { forDate: forDate } })
      .toPromise();
  }

  public structurebyorgid(id: number): Promise<TreeNode[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/structurebyorgid/${id}`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/structurebyorgid/${id}`).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public getRegionInfo(id?: number): Promise<ICustomer> {
    let url = `${this.url}/${id}/regionInfo`;

    return this.handleRequest<ICustomer>(
      this.http.get<ICustomer>(url),
      this.handlersFactory.getForReadById(this.entityTitle),
    );
  }

  public getpostcodesstructure(): Promise<TreeNode[]> {
    return this.handleRequest(
      this.http.get<any>(`${this.url}/postcodesstructure`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/postcodesstructure`).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public getregionpostcodes(id: number): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/region/${id}/postcodes`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/region/${id}/postcodes`).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public geteregionpostcodes(id: number): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/eregion/${id}/postcodes`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/eregion/${id}/postcodes`).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public getdepotpostcodes(id: number): Promise<any[]> {
    return this.handleRequest(
      this.http.get<any[]>(`${this.url}/region/depot/${id}/postcodes`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/region/depot/${id}/postcodes`).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public getEngineiusRegionFromPostcode(postcode: string): Promise<IERegion> {
    return this.handleRequest(
      this.http.get<IERegion>(`${this.url}/engineiusRegionFromPostcode/${postcode}`),
      this.handlersFactory.getDefault(),
    );
  }

  public getOrg(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/getorgdetails/${id}`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/${id}`).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public getDepot(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/region/depot/${id}`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/region/depot/${id}`).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public updateDepot(depot: any): Promise<any> {
    let successMsg =
      typeof depot.id != undefined && depot.id != 0 ? 'Depot updated.' : 'Depot added.';

    return this.handleRequest(
      this.http.put(`${this.url}/region/depot`, depot),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }

  public updateDepotPostcodes(depotid: number, codes: any[]): Promise<any> {
    let successMsg = 'Postcode saved.';
    return this.handleRequest(
      this.http.put(`${this.url}/region/depot/${depotid}/postcodes`, codes),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
    //return this.http.put(`${this.url}/region/depot/${depotid}/postcodes`, codes).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public updateRegionPostcodes(regionid: number, codes: any[]): Promise<any> {
    let successMsg = 'Postcode saved.';
    return this.handleRequest(
      this.http.put(`${this.url}/region/${regionid}/postcodes`, codes),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
    //return this.http.put(`${this.url}/region/${regionid}/postcodes`, codes).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public updateERegionPostcodes(regionid: number, codes: any[]): Promise<any> {
    let successMsg = 'Postcode added to region.';
    return this.handleRequest(
      this.http.put(`${this.url}/eregion/${regionid}/postcodes`, codes),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
    //return this.http.put(`${this.url}/eregion/${regionid}/postcodes`, codes).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public updateTeam(team: any): Promise<any> {
    let successMsg = typeof team.id != undefined && team.id != 0 ? 'Team updated.' : 'Team added.';

    return this.handleRequest(
      this.http.put(`${this.url}/department/team`, team),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }

  public updateRegion(region: any): Promise<any> {
    let successMsg =
      typeof region.id != undefined && region.id != 0 ? 'Region updated.' : 'Region added.';

    return this.handleRequest(
      this.http.put(`${this.url}/region`, region),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }

  public updateDepartment(department: any): Promise<any> {
    let successMsg =
      typeof department.id != undefined && department.id != 0
        ? 'Department updated.'
        : 'Department added.';

    return this.handleRequest(
      this.http.put<string>(`${this.url}/department`, department),
      this.handlersFactory.getForUpdate(this.entityTitle),
      successMsg,
    );
  }

  public getRegion(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/region/${id}`),
      this.handlersFactory.getDefault(),
    );
  }

  public getDepartment(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/department/${id}`),
      this.handlersFactory.getDefault(),
    );
  }

  public getAllDepartments(): Promise<any> {
    return this.handleRequest(
      this.http.get(`/api/customers/getAllDepartments`),
      this.handlersFactory.getDefault(),
    );
  }

  public getAllCustomerDepartmentPriorities(): Promise<any> {
    return this.handleRequest(
      this.http.get(`/api/customer-priority/getAllCustomerDepartmentPriorites`),
      this.handlersFactory.getDefault(),
    );
  }

  public getTeam(id: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/department/team/${id}`),
      this.handlersFactory.getDefault(),
    );
  }

  public updateChecklist(checklist: any) {
    return this.handleRequest(
      this.http.post(`${this.url}/updateChecklist`, checklist),
      this.handlersFactory.getDefault(),
    );
  }

  public updateChecklistItem(checklistItem: IOrganisationSpecificSteps) {
    return this.handleRequest(
      this.http.put(`${this.url}/updateChecklistItem`, checklistItem),
      this.handlersFactory.getDefault(),
    );
  }

  public addChecklist(newChecklist: any): Promise<number> {
    return this.handleRequest(
      this.http.post<number>(`${this.url}/addChecklist`, newChecklist),
      this.handlersFactory.getForCreate(this.entityTitle),
      'New checklist created',
    );
  }

  /**
   * Checks if the checklist has been deleted previously.
   * i.e. A checklist with this name/id already exists but is soft-deleted.
   * @param newChecklist The checklist details
   * @return Resolves true if the checklist exists but is soft-deleted
   */
  public hasChecklistBeenDeleted(newChecklist: any): Promise<boolean> {
    return this.http.post<boolean>(`${this.url}/hasChecklistBeenDeleted`, newChecklist).toPromise();
  }

  public reinstateChecklist(checklistItem: any) {
    return this.handleRequest(
      this.http.post(`${this.url}/reinstateChecklist`, checklistItem),
      this.handlersFactory.getDefault(),
    );
  }

  public deleteChecklist(id: number) {
    return this.handleRequest(
      this.http.get(`${this.url}/deleteChecklist?id=${id}`),
      this.handlersFactory.getForDelete('Checklist'),
    );
  }

  /**
   * Posts a new step
   * @todo typo here, on the object it's isPickup, on the interface it's isPickUp
   */
  saveStep(step: IOrganisationSpecificSteps): Promise<any> {
    const message = (step as any).isPickup
      ? 'Step added to pick-up checklist.'
      : 'Step added to drop-off checklist.';
    return this.handleRequest(
      this.http.post(`${this.url}/addspecificstep`, step),
      this.handlersFactory.getForUpdate(this.entityTitle),
      message,
    ).catch(this.handleError);
  }

  public updateSpecificStepsList(specifictStepList: any): Promise<any> {
    return this.handleRequest(
      this.http.post(`${this.url}/updatespecificstepslist`, specifictStepList),
      this.handlersFactory.getDefault(),
    ).catch(this.handleError);
  }

  public deleteSpecificStep(id: number, isPickup: boolean): Promise<any> {
    let message = 'Step deleted.';
    return this.handleRequest(
      this.http.get('/api/org/deletespecificstep?id=' + id + '&isPickup=' + isPickup),
      this.handlersFactory.getForUpdate(this.entityTitle),
      message,
    ).catch(this.handleError);
  }

  public getCurrentUserOrgTypeId(): Promise<number> {
    return this.handleRequest(
      this.http.get<number>('/api/org/getcurrentuserorganisationtype'),
      this.handlersFactory.getDefault(),
    ).catch(this.handleError);
  }

  public getCurrentUserOrgSubTypeId(): Promise<number> {
    return this.handleRequest(
      this.http.get<number>('/api/org/getcurrentuserorganisationsubtype'),
      this.handlersFactory.getDefault(),
    ).catch(this.handleError);
  }

  public getStructureByOrgType(orgId: number, orgType: number): Promise<any> {
    return this.handleRequest(
      this.http.get('api/org/structurebyorgtype?orgId=' + orgId + '&orgTypeId=' + orgType),
      this.handlersFactory.getDefault(),
    );
  }

  public getAllOrg(filter: IOrganisationFilter): Promise<any> {
    let params = this.constructHttpParams(filter);

    return this.handleRequest(
      this.http.get('api/org/allorg', { params: params }),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getAllCustomers(): Promise<any> {
    return this.handleRequest(
      this.http.get('api/org/allcust'),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getAllCustomersLookup(): Promise<any> {
    return this.handleRequest(
      this.http.get('api/org/allcustLookup'),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getHandoverTimes(organisationId: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`api/org/${organisationId}/organisationHandoverTimes`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public setHandoverTimes(organisationId: number, handoverTimes: IHandoverTime[]): Promise<any> {
    return this.handleRequest(
      this.http.post(`api/org/${organisationId}/organisationHandoverTimes`, handoverTimes),
      this.handlersFactory.getForReadAll(),
    );
  }

  getSubTypes(): Promise<any> {
    return this.handleRequest(
      this.http.get(`${this.url}/getsubtypes`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/getsubtypes`).toPromise().then(this.extractData).catch(this.handleError);
  }

  public extractData(res) {
    return res;
  }

  public getUserOrganisationLevel(): Promise<IUserOrganisationLevel> {
    return this.handleRequest(
      this.http.get<any>(`${this.url}/getUserOrganisationLevel`),
      this.handlersFactory.getDefault(),
    );
    //return this.http.get(`${this.url}/getUserOrganisationLevel`).toPromise()
    //    .then(this.extractData)
    //    .catch(this.handleError);
  }

  public getCurrentUserOrgName(): Promise<any> {
    return this.handleRequest(
      this.http.get('/api/org/getcurrentuserorganisationname'),
      this.handlersFactory.getDefault(),
    );
  }

  /**
   * The API key is a secret and is not returned as part of the organisation, it has to be
   * requested explicitly from this dedicated endpoint.
   */
  public getApiKey(organisationId: number): Promise<string> {
    return this.handleRequest(
      this.http.get(`${this.url}/apikey?organisationId=${organisationId}`, {
        responseType: 'text',
      }),
      this.handlersFactory.getDefault(),
    );
  }

  public createApiKey(organisationId: number): Promise<any> {
    return this.handleRequest(
      this.http.post(`${this.url}/apikey?organisationId=${organisationId}`, null, {
        responseType: 'text',
      }),
      this.handlersFactory.getDefault(),
    );
  }

  public getSelectList(filterCommand: IOrganisationFilter): Promise<SelectItem[]> {
    return this.http
      .get<any>(`${this.url}/selectlist`, { params: this.constructHttpParams(filterCommand) })
      .toPromise()
      .then((res) => {
        var l = [];
        if (res != null) {
          res.forEach((item) => {
            l.push({ label: item.name, value: item.id });
          });
        }
        return l;
      });
  }

  public getCrossFilter(filterCommand: IOrganisationFilter): Promise<IFacetedSupplier[]> {
    return this.http
      .get<any>(`${this.url}/crossFilterList`, { params: this.constructHttpParams(filterCommand) })
      .toPromise();
  }

  /** Get list of checklist items for the current user's organisation */
  public getChecklists(activeOnly?: boolean): Promise<IOrganisationSpecificStepGroup[]> {
    let object = {};

    if (activeOnly != null) {
      object['activeOnly'] = activeOnly;
    }

    let params = this.constructHttpParams(object);

    return this.handleRequest(
      this.http.get<any>(`${this.url}/checklists`, { params: params }),
      this.handlersFactory.getDefault(),
    );
  }

  public getCallerInfo(phoneNumber): Promise<ICallerInfo> {
    return this.http.get<ICallerInfo>(`${this.url}/getCallerInfo/${phoneNumber}`).toPromise();
  }

  public getBasicInfos(): Promise<IBasicOrganisationInfo[]> {
    return this.http.get<IBasicOrganisationInfo[]>(`${this.url}/getBasicInfos`).toPromise();
  }

  public getTelephonyInfo(organisationId: number): Promise<IOrganisationTelephonyInfo> {
    return this.http
      .get<IOrganisationTelephonyInfo>(`${this.url}/getTelephonyInfo/${organisationId}`)
      .toPromise();
  }

  public getOrganisationContractTypeHistory(organisationId: number): Promise<any> {
    return this.handleRequest(
      this.http.get(`/api/org/getContractTypeHistory/${organisationId}`),
      this.handlersFactory.getForReadAll(),
    );
  }

  public getOrganisationHasMultipleDrivers(): Promise<boolean> {
    return this.http.get<boolean>(`/api/org/organisation-has-multiple-drivers`).toPromise();
  }

  public updateOrganisationContractTypeHistory(histories: IContractTypeHistory[]) {
    return this.handleRequest(
      this.http.post(`${this.url}/updateContractTypeHistory`, histories),
      this.handlersFactory.getDefault(),
    );
  }

  public getServiceDeliveryGroups(): Promise<IServiceDeliveryGroup[]> {
    return this.http
      .get<IServiceDeliveryGroup[]>(`${this.url}/getServiceDeliveryGroups`)
      .toPromise();
  }

  public addServiceDeliveryGroup(dto: IServiceDeliveryGroup) {
    return this.handleRequest(
      this.http.post(`${this.url}/addServiceDeliveryGroup`, dto),
      this.handlersFactory.getDefault(),
    );
  }

  public updateServiceDeliveryGroup(dto: IServiceDeliveryGroup) {
    return this.handleRequest(
      this.http.put(`${this.url}/updateServiceDeliveryGroup`, dto),
      this.handlersFactory.getDefault(),
    );
  }

  public updateSupplierSignupType(signupData: {
    organisationId: number;
    supplierSignupTypeId: number;
  }) {
    console.log('SUPPLIER SIGNUP: ', signupData);

    return this.handleRequest(
      this.http.put(`${this.url}/updateSupplierSignupType`, signupData),
      this.handlersFactory.getDefault(),
    );
  }

  public deleteServiceDeliveryGroup(id: number) {
    return this.handleRequest(
      this.http.delete(`${this.url}/deleteServiceDeliveryGroup/${id}`),
      this.handlersFactory.getForDelete('Service Delivery Group'),
    );
  }

  getSupplierClassifications(): Promise<any> {
    return Promise.resolve();
  }

  getSupplierHours(): Promise<any> {
    return Promise.resolve();
  }

  getSupplierTags(): Promise<any> {
    return Promise.resolve();
  }

  patchOrganisationPhoneNumbers(organisationId: number, patch: Operation[]): Promise<any> {
    return this.http.patch(`/api/org/${organisationId}/phone-numbers`, patch).toPromise();
  }

  getImpersonationUsersList(organisationId: number): Promise<IImpersonationUserInfo[]> {
    return this.http
      .get<IImpersonationUserInfo[]>(`${this.url}/${organisationId}/impersonation-users-list`)
      .toPromise();
  }

  getActivationData(organisationId: number): Promise<IOrganisationActivationData> {
    return this.http
      .get<IOrganisationActivationData>(`${this.url}/${organisationId}/activation-data`)
      .toPromise();
  }

  getOrganisationInfoByTaskType(taskType: number): Promise<IOrganisationInfo[]> {
    return this.http
      .get<IOrganisationInfo[]>(`${this.url}/${taskType}/organisationInfoByTaskType`)
      .toPromise();
  }
}
